// League fixtures (Phase 2) — generate, view, clear.
//
// Generating writes two things per match: a leagueMatches doc, and a
// reservations doc holding the court. The reservation is what makes a league
// night visible on the Schedule page and what makes createMemberBooking refuse
// to book over it — without it a member could book a court a match is on.
//
// Both are written in one batch, and cleared together, so the calendar can
// never disagree with the fixture list about which courts are spoken for.
import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  collection, query, where, onSnapshot, doc, getDoc, getDocs, writeBatch, updateDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Box, Container, Typography, Chip, CircularProgress, Alert, Button, Card,
  CardContent, IconButton, Table, TableContainer, TableBody, TableCell,
  TableHead, TableRow, Paper, Divider, TextField, Tabs, Tab,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { buildSchedule, swapTeams, swapMatchSlots } from '../utils/leagueSchedule';
import { computeStandings, isPlayed, fmtPointRatio, applyStandingsOverride, seedList } from '../utils/leagueStandings';
import { buildBracket, roundName, advancementTarget, winnerOf, groupRounds, isBye } from '../utils/leaguePlayoff';
import BracketConnector, { BRACKET_GAP } from '../components/BracketConnector';

const courtLabel = (c) => `Court ${String(c).replace('court-', '')}`;
const fmtTime = (t) => {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(t || ''));
  if (!m) return t || '';
  const h = Number(m[1]);
  return `${h % 12 || 12}:${m[2]} ${h >= 12 ? 'PM' : 'AM'}`;
};
const fmtDate = (iso) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(iso || ''))) return '–';
  const [y, m, d] = iso.split('-').map(Number);
  return new Date(y, m - 1, d).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
};

export default function LeagueSchedule() {
  const { leagueId } = useParams();
  const navigate = useNavigate();
  const [league, setLeague] = useState(null);
  const [teams, setTeams] = useState([]);
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  // Courts are part of the league's definition, alongside its days and slots —
  // all three decide how many matches a week can hold, so they belong together
  // rather than one of them being chosen afresh at generation time.
  const courts = (Array.isArray(league?.courts) && league.courts.length)
    ? league.courts
    : ['court-1', 'court-2'];   // leagues defined before courts were settable
  const [preview, setPreview] = useState(null);
  // The payload lives in a ref, not state: setState is asynchronous, and a
  // drop that lands before React re-renders would read a stale null and
  // silently do nothing. The state copy exists only to drive the visuals.
  const dragRef = useRef(null);
  const [drag, setDrag] = useState(null);     // mirror, for highlighting only
  const [over, setOver] = useState(null);     // current drop target key
  const [tab, setTab] = useState(0);
  const [scores, setScores] = useState({});   // matchId -> { home, away } being typed
  const startDrag = (payload) => { dragRef.current = payload; setDrag(payload); };
  const endDrag = () => { dragRef.current = null; setDrag(null); setOver(null); };

  useEffect(() => {
    getDoc(doc(db, 'leagues', leagueId))
      .then(s => { if (s.exists()) setLeague({ id: s.id, ...s.data() }); })
      .catch(e => setError('Could not load the league: ' + e.message));
  }, [leagueId]);

  useEffect(() => {
    const unsubT = onSnapshot(query(collection(db, 'leagueTeams'), where('leagueId', '==', leagueId)),
      s => setTeams(s.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (a.name || '').localeCompare(b.name || '', undefined, { numeric: true }))));
    const unsubM = onSnapshot(query(collection(db, 'leagueMatches'), where('leagueId', '==', leagueId)),
      s => {
        const rows = s.docs.map(d => ({ id: d.id, ...d.data() }));
        rows.sort((a, b) => (a.week - b.week)
          || String(a.date).localeCompare(String(b.date))
          || String(a.startTime).localeCompare(String(b.startTime))
          || String(a.court).localeCompare(String(b.court)));
        setMatches(rows);
        setLoading(false);
      }, e => { setError('Could not load fixtures: ' + e.message); setLoading(false); });
    return () => { unsubT(); unsubM(); };
  }, [leagueId]);

  const flash = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 5000); };

  const doPreview = () => {
    setError('');
    if (!league) return;
    if (teams.length < 2) { setError('Add at least two teams first.'); return; }
    if (!courts.length) { setError('This league has no courts set — add them on the league.'); return; }
    const r = buildSchedule({
      teams, days: league.days || [], timeSlots: league.timeSlots || [],
      startDate: league.startDate, numWeeks: league.numWeeks, courts,
    });
    if (!r.matches.length) { setError('Nothing to schedule — check the league has days and time slots set.'); return; }
    setPreview(r);
  };

  const commit = async () => {
    if (!preview) return;
    setBusy(true); setError('');
    try {
      // Chunked well under Firestore's 500-write batch limit: each match is
      // two documents, so 200 matches per batch.
      const chunk = 200;
      for (let i = 0; i < preview.matches.length; i += chunk) {
        const batch = writeBatch(db);
        preview.matches.slice(i, i + chunk).forEach(m => {
          const matchRef = doc(collection(db, 'leagueMatches'));
          const resRef = doc(collection(db, 'reservations'));
          batch.set(matchRef, {
            ...m, leagueId, leagueName: league.name || '',
            reservationId: resRef.id,
            homeScore: null, awayScore: null,
            createdAt: serverTimestamp(),
          });
          // Same shape tournaments use, so the calendar renders it already.
          batch.set(resRef, {
            type: 'league',
            title: `${league.name} — ${m.homeTeamName} v ${m.awayTeamName}`,
            leagueId, leagueMatchId: matchRef.id,
            date: m.date, day: m.day,
            startTime: m.startTime, endTime: m.endTime,
            courts: [m.court],
            notes: `League week ${m.week}`,
            createdAt: serverTimestamp(),
          });
        });
        await batch.commit();
      }
      flash(`Scheduled ${preview.matches.length} matches and blocked the courts.`);
      setPreview(null);
    } catch (e) {
      console.error('Schedule commit failed', e);
      setError('Could not save the schedule: ' + e.message);
    }
    setBusy(false);
  };

  // Clearing must take the reservations with it, or the courts stay blocked
  // for matches that no longer exist.
  const clearAll = async () => {
    if (!window.confirm(
      `Delete all ${matches.length} fixtures and release the courts they hold?`)) return;
    setBusy(true); setError('');
    try {
      const resSnap = await getDocs(query(collection(db, 'reservations'), where('leagueId', '==', leagueId)));
      const refs = [...matches.map(m => doc(db, 'leagueMatches', m.id)), ...resSnap.docs.map(d => d.ref)];
      for (let i = 0; i < refs.length; i += 400) {
        const batch = writeBatch(db);
        refs.slice(i, i + 400).forEach(r => batch.delete(r));
        await batch.commit();
      }
      flash('Schedule cleared and courts released.');
    } catch (e) {
      console.error('Clear failed', e);
      setError('Could not clear the schedule: ' + e.message);
    }
    setBusy(false);
  };

  // One batch for both documents of both matches: a fixture and the
  // reservation holding its court must never be half-moved.
  const applyPatches = async (patches, what) => {
    if (!patches) return;                      // dropped on itself
    setBusy(true); setError('');
    try {
      const batch = writeBatch(db);
      patches.forEach(p => {
        batch.update(doc(db, 'leagueMatches', p.matchId), p.match);
        if (p.reservationId) batch.update(doc(db, 'reservations', p.reservationId), p.reservation);
      });
      await batch.commit();
      flash(what);
    } catch (e) {
      console.error('Swap failed', e);
      setError('Could not apply that change: ' + e.message);
    }
    setBusy(false);
  };

  const onDropTeam = (target, targetSlot) => {
    const d = dragRef.current;
    if (!d || d.kind !== 'team') return;
    const src = matches.find(m => m.id === d.matchId);
    if (!src) return;
    applyPatches(swapTeams(src, d.slot, target, targetSlot, league?.name || ''), 'Teams swapped.');
  };

  const onDropMatch = (target) => {
    const d = dragRef.current;
    if (!d || d.kind !== 'match') return;
    const src = matches.find(m => m.id === d.matchId);
    if (!src) return;
    applyPatches(swapMatchSlots(src, target), 'Matches swapped.');
  };

  // A team's score, entered beside that team rather than in a pair of boxes off
  // to the right — there the left box meant the left team only by convention.
  //
  // Not draggable: typing in a cell inside a draggable row would otherwise
  // start a row drag on mousedown.
  const scoreBox = (m, slot) => (
    <Box draggable={false} onDragStart={e => e.preventDefault()} sx={{ display: 'flex' }}>
      <TextField size="small" type="number" sx={{ width: 58 }}
        inputProps={{ min: 0, step: 1, style: { textAlign: 'center', padding: '4px 6px' } }}
        value={scores[m.id]?.[slot] ?? ((slot === 'home' ? m.homeScore : m.awayScore) ?? '')}
        onChange={e => setScores(s2 => ({ ...s2, [m.id]: { ...s2[m.id], [slot]: e.target.value } }))} />
    </Box>
  );

  // A team chip: draggable, and a drop target for another team.
  const teamChip = (m, slot) => {
    const key = `${m.id}:${slot}`;
    const name = slot === 'home' ? m.homeTeamName : m.awayTeamName;
    return (
      <Chip size="small" label={name} draggable
        onDragStart={e => {
          e.stopPropagation();               // do not also start a match drag
          startDrag({ kind: 'team', matchId: m.id, slot });
          e.dataTransfer.effectAllowed = 'move';
          e.dataTransfer.setData('text/plain', key);
        }}
        onDragEnd={endDrag}
        onDragOver={e => {
          if (dragRef.current?.kind !== 'team') return;
          e.preventDefault(); e.stopPropagation(); setOver(key);
        }}
        onDragLeave={() => setOver(o => (o === key ? null : o))}
        onDrop={e => { e.preventDefault(); e.stopPropagation(); onDropTeam(m, slot); endDrag(); }}
        sx={{
          cursor: 'grab',
          outline: over === key ? '2px solid #1E3A5F' : 'none',
          opacity: drag?.kind === 'team' && drag.matchId === m.id && drag.slot === slot ? 0.4 : 1,
        }} />
    );
  };

  // Scores live on the match. The reservation is untouched — a result changes
  // nothing about when or where the court was used.
  const saveScore = async (m) => {
    const draft = scores[m.id] || {};
    const h = draft.home ?? (m.homeScore ?? '');
    const a = draft.away ?? (m.awayScore ?? '');
    // Blank both to un-enter a result; one blank is half a result and refused.
    const bothBlank = String(h) === '' && String(a) === '';
    if (!bothBlank && (String(h) === '' || String(a) === '')) {
      setError('Enter both scores, or clear both to remove the result.');
      return;
    }
    try {
      await updateDoc(doc(db, 'leagueMatches', m.id), {
        homeScore: bothBlank ? null : (Number(h) || 0),
        awayScore: bothBlank ? null : (Number(a) || 0),
      });
      setScores(s2 => { const n = { ...s2 }; delete n[m.id]; return n; });
      if (m.stage === 'playoff' && !bothBlank) {
        await advanceWinner({ ...m, homeScore: Number(h) || 0, awayScore: Number(a) || 0 });
      }
      flash(bothBlank ? 'Result cleared.' : 'Result saved.');
    } catch (e) {
      setError('Could not save the result: ' + e.message);
    }
  };

  // Computed order, then the admin's manual finishing order on top. The
  // override is what seeds the play-off, so it has to win over the maths.
  // Play-off fixtures are in the same collection, tagged with a stage. They
  // must NOT feed the league table — the season decides the seeding, and a
  // knockout result folding back into it would reshuffle the very order the
  // bracket was drawn from.
  const seasonMatches = matches.filter(m => m.stage !== 'playoff');
  const playoffMatches = matches.filter(m => m.stage === 'playoff');
  // Push a play-off winner into the next round's slot. Called after a result
  // is saved rather than derived on read, so the bracket a player sees is the
  // stored one and not something recomputed differently on another screen.
  const advanceWinner = async (m) => {
    const totalRounds = Math.max(0, ...playoffMatches.map(x => (x.round ?? 0) + 1));
    const target = advancementTarget(m, totalRounds);
    const w = winnerOf(m);
    if (!target || !w) return;      // the final, or a draw
    const next = playoffMatches.find(x => x.round === target.round && x.index === target.index);
    if (!next) return;
    try {
      await updateDoc(doc(db, 'leagueMatches', next.id), {
        [`${target.slot}TeamId`]: w.id,
        [`${target.slot}TeamName`]: w.name,
      });
    } catch (e) { setError('Result saved, but advancing the winner failed: ' + e.message); }
  };

  const standings = applyStandingsOverride(computeStandings(teams, seasonMatches),
    league?.standingsOverride);
  const isOverridden = Array.isArray(league?.standingsOverride) && league.standingsOverride.length > 0;

  const saveOrder = async (ids) => {
    try {
      await updateDoc(doc(db, 'leagues', leagueId), { standingsOverride: ids });
      setLeague(l => ({ ...l, standingsOverride: ids }));
      flash('Final standings saved.');
    } catch (e) { setError('Could not save the order: ' + e.message); }
  };

  const resetOrder = async () => {
    try {
      await updateDoc(doc(db, 'leagues', leagueId), { standingsOverride: [] });
      setLeague(l => ({ ...l, standingsOverride: [] }));
      flash('Back to the computed standings.');
    } catch (e) { setError('Could not reset: ' + e.message); }
  };

  // Move a row to another position and persist the whole resulting order.
  const reorder = (fromId, toId) => {
    if (fromId === toId) return;
    const ids = standings.map(r => r.teamId);
    const from = ids.indexOf(fromId), to = ids.indexOf(toId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]);
    saveOrder(ids);
  };
  const playedCount = seasonMatches.filter(isPlayed).length;

  const generateBracket = async () => {
    const seeds = seedList(standings).filter(x => x.teamId);
    if (seeds.length < 2) { setError('Need at least two teams to seed a bracket.'); return; }
    if (playoffMatches.length && !window.confirm(
      'Replace the existing bracket? Results already entered will be lost.')) return;
    setBusy(true); setError('');
    try {
      const { rounds } = buildBracket(seeds.map(x => ({ id: x.teamId, name: x.name })));
      const slot = (league?.timeSlots || [])[0] || { startTime: '18:30', lengthMinutes: 60 };
      const batch = writeBatch(db);
      playoffMatches.forEach(m => batch.delete(doc(db, 'leagueMatches', m.id)));
      rounds.forEach((round, r) => round.forEach(m => {
        batch.set(doc(collection(db, 'leagueMatches')), {
          leagueId, leagueName: league?.name || '',
          stage: 'playoff', round: r, index: m.index,
          // Every round is played on the one play-off date. No reservation is
          // written: the court a knockout lands on depends on who survives, so
          // blocking one now would hold a court for a match that may not happen.
          date: league?.playoffDate || '', day: '', court: '',
          startTime: slot.startTime,
          endTime: slot.startTime, week: null,
          homeTeamId: m.home?.id || null, homeTeamName: m.home?.name || '',
          awayTeamId: m.away?.id || null, awayTeamName: m.away?.name || '',
          homeSeed: m.homeSeed ?? null, awaySeed: m.awaySeed ?? null,
          homeScore: null, awayScore: null,
          createdAt: serverTimestamp(),
        });
      }));
      await batch.commit();
      flash(`Bracket drawn — ${rounds.flat().length} matches across ${rounds.length} rounds.`);
    } catch (e) {
      setError('Could not draw the bracket: ' + e.message);
    }
    setBusy(false);
  };

  const { rounds: bracketRounds, totalRounds } = groupRounds(playoffMatches);

  const byWeek = seasonMatches.reduce((acc, m) => {
    (acc[m.week] = acc[m.week] || []).push(m); return acc;
  }, {});

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3, flexWrap: 'wrap' }}>
        <IconButton onClick={() => navigate('/tournament-admin/leagues')}><ArrowBackIcon /></IconButton>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight="bold">{league?.name || 'League'} — Schedule</Typography>
          <Typography variant="body2" color="text.secondary">
            {teams.length} teams · {matches.length} fixtures
            {league?.dayTimeDisplay ? ` · ${league.dayTimeDisplay}` : ''}
          </Typography>
        </Box>
        {matches.length > 0 && (
          <Button color="error" variant="outlined" startIcon={<DeleteSweepIcon />}
            onClick={clearAll} disabled={busy}>Clear schedule</Button>
        )}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      {matches.length === 0 && (
        <Card elevation={2} sx={{ borderRadius: 3, mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Generate fixtures</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              A round-robin across {league?.numWeeks || 0} weeks. Every team plays every other once
              per rotation; with an odd number of teams, one gets a bye each week.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Courts: {courts.map(courtLabel).join(', ')} — set on the league itself.
            </Typography>
            <Button variant="contained" startIcon={<AutoAwesomeIcon />} onClick={doPreview} disabled={busy}>
              Preview schedule
            </Button>
          </CardContent>
        </Card>
      )}

      {preview && (
        <Card elevation={3} sx={{ borderRadius: 3, mb: 3, border: '2px solid #1E3A5F' }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700}>
              Preview — {preview.matches.length} matches
            </Typography>
            {preview.unplaced.length > 0 && (
              <Alert severity="warning" sx={{ my: 1 }}>
                {preview.unplaced.length} fixture(s) don't fit the days, slots and courts available and
                would not be scheduled. Add a court or a time slot, or shorten the season.
              </Alert>
            )}
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Nothing is written until you confirm. This also creates a reservation per match so the
              courts show as booked.
            </Typography>
            <Box sx={{ maxHeight: 220, overflowY: 'auto', mb: 1 }}>
              {preview.matches.slice(0, 12).map((m, i) => (
                <Typography key={i} variant="body2">
                  W{m.week} · {fmtDate(m.date)} · {fmtTime(m.startTime)} · {courtLabel(m.court)} ·{' '}
                  {m.homeTeamName} v {m.awayTeamName}
                </Typography>
              ))}
              {preview.matches.length > 12 && (
                <Typography variant="caption" color="text.secondary">
                  … and {preview.matches.length - 12} more
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="contained" onClick={commit} disabled={busy}>
                {busy ? 'Saving…' : `Create ${preview.matches.length} matches`}
              </Button>
              <Button onClick={() => setPreview(null)} disabled={busy}>Cancel</Button>
            </Box>
          </CardContent>
        </Card>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : matches.length === 0 ? (
        <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
          <Typography color="text.secondary">
            No fixtures yet{teams.length < 2 ? ' — add teams first.' : '.'}
          </Typography>
        </Paper>
      ) : (
        <>
        {standings.length > 0 && (
          <Paper elevation={1} sx={{ p: 1.5, borderRadius: 2, mb: 2, overflowX: 'auto' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Standings{isOverridden ? ' (final, set by hand)' : ''} — seeds the play-off
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {standings.map((r, i) => (
                <Chip key={r.teamId} size="small"
                  color={i === 0 ? 'primary' : 'default'}
                  variant={i === 0 ? 'filled' : 'outlined'}
                  label={`${i + 1}. ${r.name} ${r.wins}-${r.losses}${r.played ? ` (${fmtPointRatio(r)})` : ''}`} />
              ))}
            </Box>
          </Paper>
        )}
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2, borderBottom: '1px solid #eee' }}>
          <Tab label={`Fixtures (${seasonMatches.length})`} />
          <Tab label={`Standings${isOverridden ? ' (final)' : ` (${playedCount} played)`}`} />
          <Tab label={`Bracket${playoffMatches.length ? ` (${playoffMatches.length})` : ''}`} />
        </Tabs>
        {tab === 2 ? (
          <Box>
            <Card elevation={2} sx={{ borderRadius: 3, mb: 2 }}>
              <CardContent sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ flexGrow: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Play-off bracket</Typography>
                  <Typography variant="caption" color="text.secondary">
                    Single elimination, seeded from the standings above. Winners advance
                    automatically as results are entered.
                    {league?.playoffDate ? ` Played ${fmtDate(league.playoffDate)}.` : ''}
                  </Typography>
                </Box>
                <Button variant="contained" startIcon={<AutoAwesomeIcon />}
                  onClick={generateBracket} disabled={busy || standings.length < 2}>
                  {playoffMatches.length ? 'Redraw bracket' : 'Draw bracket'}
                </Button>
              </CardContent>
            </Card>

            {playoffMatches.length === 0 ? (
              <Paper elevation={1} sx={{ p: 5, textAlign: 'center', borderRadius: 2, border: '1px dashed #ccc' }}>
                <Typography color="text.secondary">
                  No bracket yet. Settle the standings first, then draw it.
                </Typography>
              </Paper>
            ) : (
              <Box sx={{ display: 'flex', gap: `${BRACKET_GAP}px`, overflowX: 'auto', pb: 1, alignItems: 'stretch' }}>
                {/* Rounds stretch to a common height and every match takes an
                    equal share of its column, which is what puts a match exactly
                    between the two that feed it. The elbows rely on it. */}
                {bracketRounds.map((round, r) => (
                  <Box key={r} sx={{ minWidth: 250, display: 'flex', flexDirection: 'column' }}>
                    <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
                      {roundName(r, totalRounds)}
                    </Typography>
                    <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                    {round.map(m => {
                      const w = winnerOf(m);
                      const bye = isBye(m);
                      const ready = m.homeTeamId && m.awayTeamId;
                      // A team's score is entered on that team's own row rather
                      // than in a pair of boxes underneath: two rows and two
                      // boxes left it to the eye to work out which was which,
                      // and the box order was the only clue.
                      const side = (slot) => {
                        const id = slot === 'home' ? m.homeTeamId : m.awayTeamId;
                        const nm = slot === 'home' ? m.homeTeamName : m.awayTeamName;
                        const sd = slot === 'home' ? m.homeSeed : m.awaySeed;
                        const sc = slot === 'home' ? m.homeScore : m.awayScore;
                        const won = w && w.id === id;
                        return (
                          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', py: 0.25 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ width: 16 }}>
                              {id ? (sd ?? '') : ''}
                            </Typography>
                            <Typography variant="body2" sx={{ flexGrow: 1, fontWeight: won ? 700 : 400,
                              color: id ? 'text.primary' : 'text.disabled' }}>
                              {/* An empty side in round one is a bye, not an
                                  opponent still to be decided. */}
                              {nm || (bye ? 'Bye' : 'TBD')}
                            </Typography>
                            {ready ? (
                              <TextField size="small" type="number" sx={{ width: 54 }}
                                inputProps={{ min: 0,
                                  style: { textAlign: 'center', padding: '3px 4px', fontWeight: won ? 700 : 400 } }}
                                value={scores[m.id]?.[slot] ?? (sc ?? '')}
                                onChange={e => setScores(s2 => ({
                                  ...s2, [m.id]: { ...s2[m.id], [slot]: e.target.value } }))} />
                            ) : (
                              <Typography variant="body2" fontWeight={won ? 700 : 400}>
                                {sc ?? ''}
                              </Typography>
                            )}
                          </Box>
                        );
                      };
                      return (
                        <Box key={m.id} sx={{ flex: 1, display: 'flex', alignItems: 'center', position: 'relative' }}>
                          {r > 0 && <BracketConnector />}
                        <Paper elevation={1}
                          sx={{ width: '100%', p: 1, borderRadius: 2,
                            borderLeft: w ? '3px solid #27AE60' : '3px solid transparent' }}>
                          {side('home')}
                          {side('away')}
                          {/* Save appears only once something has been typed —
                              a permanently greyed button on every tile was most
                              of what made them look busy. */}
                          {ready && scores[m.id] && (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                              <Button size="small" variant="contained" onClick={() => saveScore(m)}>Save</Button>
                            </Box>
                          )}
                          {!ready && !m.homeTeamId && !m.awayTeamId && (
                            <Typography variant="caption" color="text.disabled">Awaiting both winners</Typography>
                          )}
                          {r === totalRounds - 1 && w && (
                            <Chip size="small" color="success" label={`${w.name} wins the league`} sx={{ mt: 0.5 }} />
                          )}
                        </Paper>
                        </Box>
                      );
                    })}
                    </Box>
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        ) : tab === 1 ? (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell><strong>#</strong></TableCell>
                  <TableCell><strong>Team</strong></TableCell>
                  <TableCell align="right"><strong>P</strong></TableCell>
                  <TableCell align="right"><strong>W</strong></TableCell>
                  <TableCell align="right"><strong>L</strong></TableCell>
                  <TableCell align="right"><strong>D</strong></TableCell>
                  <TableCell align="right"><strong>PF</strong></TableCell>
                  <TableCell align="right"><strong>PA</strong></TableCell>
                  <TableCell align="right"><strong>Diff</strong></TableCell>
                  <TableCell align="right"><strong>Ratio</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {standings.map((r, i) => (
                  <TableRow key={r.teamId} hover draggable
                    onDragStart={e => {
                      startDrag({ kind: 'standing', teamId: r.teamId });
                      e.dataTransfer.effectAllowed = 'move';
                      e.dataTransfer.setData('text/plain', r.teamId);
                    }}
                    onDragEnd={endDrag}
                    onDragOver={e => {
                      if (dragRef.current?.kind !== 'standing') return;
                      e.preventDefault(); setOver(r.teamId);
                    }}
                    onDragLeave={() => setOver(o => (o === r.teamId ? null : o))}
                    onDrop={e => {
                      e.preventDefault();
                      const d = dragRef.current;
                      if (d?.kind === 'standing') reorder(d.teamId, r.teamId);
                      endDrag();
                    }}
                    sx={{
                      cursor: 'grab',
                      backgroundColor: over === r.teamId && drag?.kind === 'standing'
                        ? 'rgba(30,58,95,0.08)' : undefined,
                      opacity: drag?.kind === 'standing' && drag.teamId === r.teamId ? 0.4 : 1,
                    }}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>
                      <strong>{r.name}</strong>
                      {r.deadlocked && !r.overridden && (
                        <Chip size="small" label="tied" color="warning" sx={{ ml: 1, height: 18 }} />
                      )}
                      {r.overridden && (
                        <Chip size="small" label="set by hand" sx={{ ml: 1, height: 18 }} />
                      )}
                    </TableCell>
                    <TableCell align="right">{r.played}</TableCell>
                    <TableCell align="right">{r.wins}</TableCell>
                    <TableCell align="right">{r.losses}</TableCell>
                    <TableCell align="right">{r.draws}</TableCell>
                    <TableCell align="right">{r.pointsFor}</TableCell>
                    <TableCell align="right">{r.pointsAgainst}</TableCell>
                    <TableCell align="right">{r.diff > 0 ? `+${r.diff}` : r.diff}</TableCell>
                    <TableCell align="right"
                      sx={{ color: r.diffPct > 0 ? 'success.main' : r.diffPct < 0 ? 'error.main' : 'text.secondary' }}>
                      {fmtPointRatio(r)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Box sx={{ p: 1.5, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1 }}>
                Drag a row to set the final finishing order — this is what seeds the play-off.
                {isOverridden ? ' These standings have been set by hand.' : ''}
              </Typography>
              {isOverridden && (
                <Button size="small" onClick={resetOrder}>Reset to computed</Button>
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, pb: 1.5, display: 'block' }}>
              USAV order: match record, then head-to-head, then point ratio (points won ÷ points lost). Set percentage does not apply — a single game to 21 means sets won equals matches won. Teams marked \"tied\" are level after every step and need a play-off or coin toss. Only fixtures with both
              scores entered count as played.
            </Typography>
          </TableContainer>
        ) : (
        <>
        <Alert severity="info" sx={{ mb: 2 }}>
          Drag a <strong>team</strong> onto another team to swap the two players' fixtures, or drag a
          whole <strong>row</strong> onto another to swap when and where those two matches are played.
          The courts they hold move with them.
        </Alert>
        {Object.keys(byWeek).map(Number).sort((a, b) => a - b).map(w => (
          <Box key={w} sx={{ mb: 3 }}>
            <Typography variant="subtitle1" fontWeight={700}>Week {w}</Typography>
            <Divider sx={{ mb: 1 }} />
            <TableContainer component={Paper} elevation={1} sx={{ borderRadius: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Time</strong></TableCell>
                    <TableCell><strong>Court</strong></TableCell>
                    <TableCell><strong>Match &amp; result</strong></TableCell>
                    <TableCell align="center" />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {byWeek[w].map(m => (
                    <TableRow key={m.id} hover draggable
                      onDragStart={e => {
                        startDrag({ kind: 'match', matchId: m.id });
                        e.dataTransfer.effectAllowed = 'move';
                        e.dataTransfer.setData('text/plain', m.id);
                      }}
                      onDragEnd={endDrag}
                      onDragOver={e => {
                        if (dragRef.current?.kind !== 'match') return;
                        e.preventDefault(); setOver(m.id);
                      }}
                      onDragLeave={() => setOver(o => (o === m.id ? null : o))}
                      onDrop={e => { e.preventDefault(); onDropMatch(m); endDrag(); }}
                      sx={{
                        cursor: 'grab',
                        backgroundColor: over === m.id && drag?.kind === 'match' ? 'rgba(30,58,95,0.08)' : undefined,
                        opacity: drag?.kind === 'match' && drag.matchId === m.id ? 0.4 : 1,
                      }}>
                      <TableCell>{fmtDate(m.date)}</TableCell>
                      <TableCell>{fmtTime(m.startTime)} – {fmtTime(m.endTime)}</TableCell>
                      <TableCell><Chip size="small" label={courtLabel(m.court)} /></TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {teamChip(m, 'home')}{scoreBox(m, 'home')}
                          <strong>v</strong>
                          {teamChip(m, 'away')}{scoreBox(m, 'away')}
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        {/* Save appears only once something has been typed, so
                            a full week of fixtures is not a wall of buttons. */}
                        {scores[m.id] && (
                          <Button size="small" variant="contained" draggable={false}
                            onDragStart={e => e.preventDefault()}
                            onClick={() => saveScore(m)}>Save</Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        ))}
        </>
        )}
        </>
      )}
    </Container>
  );
}
